import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import { AlignRight as Bars3BottomRightIcon, Phone as PhoneIcon } from 'lucide-react';
import React from 'react';

// Verification-method picker. Server tells us which channels (email,
// phone) are available to deliver a one-time code; user picks one,
// we POST it back, server sends the code, then RequestCodeInput
// collects it. Layout is a narrow card with a brand-color banner
// (the kiosk's welcome panel rotated and stacked on top of the
// action) over a white method-card list.

type Methods = {
  phone?: string;
  email?: string;
  language?: 'en' | 'es';
};

const verbiage = {
  en: {
    eyebrow: 'Verify it’s you',
    title: 'Send me a code',
    body: 'Choose where you’d like us to send a one-time code. Once you receive it, enter it on the next screen to continue.',
    via: 'Send to',
    sms: 'Text message',
    email: 'Email',
    invalidCode: 'That code didn’t match. Please request a new one.',
    error: 'Something went wrong. Please try again.',
    noMethods: 'No contact methods available.',
  },
  es: {
    eyebrow: 'Verifique su identidad',
    title: 'Enviarme un código',
    body: 'Elija dónde desea recibir un código único. Una vez que lo reciba, ingréselo en la siguiente pantalla.',
    via: 'Enviar a',
    sms: 'Mensaje de texto',
    email: 'Correo electrónico',
    invalidCode: 'El código no es válido. Solicite uno nuevo.',
    error: 'Algo salió mal. Inténtelo de nuevo.',
    noMethods: 'No hay métodos de contacto disponibles.',
  },
};

type Props = {
  methods: Methods;
  primaryColor?: string;
  loading?: boolean;
  error?: string | null;
  invalidCode?: boolean;
  // Backend `sendVerificationCommunication` keys off
  // `CommunicationMethodTypes` — `'text'` for SMS and `'email'` for
  // email. Anything else is silently dropped, so do not change these
  // values without updating the helper too.
  onSubmitMethod: (method: 'text' | 'email') => void;
};

const maskPhone = (phone?: string) => {
  if (!phone) return '';

  const digits = phone.replace(/\D/g, '').slice(-4);

  return `••• ••• ${digits || '••••'}`;
};

const maskEmail = (email?: string) => {
  if (!email) return '';

  const [user, domain] = email.split('@');

  if (!domain) return email;

  const head = user.slice(0, 1);
  const tail = user.slice(-1);

  return `${head}${'•'.repeat(Math.max(1, user.length - 2))}${tail}@${domain}`;
};

const RequestAuth: React.FC<Props> = ({
  methods,
  primaryColor = '#77bd43',
  loading,
  error,
  invalidCode,
  onSubmitMethod,
}) => {
  const language: 'en' | 'es' = methods.language === 'es' ? 'es' : 'en';
  const v = verbiage[language];

  return (
    <main className="min-h-dvh flex flex-col items-center justify-center antialiased bg-gray-50 dark:bg-[var(--color-bg-page)] px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="w-full max-w-md">
        <div className="rounded-3xl overflow-hidden ring-1 ring-gray-950/5 dark:ring-white/10 bg-white dark:bg-white/5">
          <div className="p-6 sm:p-8 text-white" style={{ backgroundColor: primaryColor }}>
            <p className="text-xs font-semibold uppercase tracking-wider text-white/75">
              {v.eyebrow}
            </p>
            <h1 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight">{v.title}</h1>
            <p className="mt-3 text-sm text-white/90 max-w-prose">{v.body}</p>
          </div>
          <div className="p-5 sm:p-6 flex flex-col gap-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {v.via}
            </p>
            <div className="flex flex-col gap-2.5">
              {methods.phone && (
                <button
                  type="button"
                  onClick={() => onSubmitMethod('text')}
                  disabled={loading}
                  className="group flex items-center gap-3 text-left rounded-2xl px-4 py-3.5 portal-card hover:ring-2 hover:ring-[--ring-color] focus-visible:ring-2 focus-visible:ring-[--ring-color] focus-visible:outline-none transition-shadow disabled:opacity-50"
                  style={{ ['--ring-color' as any]: primaryColor } as React.CSSProperties}
                >
                  <span
                    className="size-9 rounded-xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${primaryColor}18`, color: primaryColor }}
                    aria-hidden="true"
                  >
                    <PhoneIcon className="size-5" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {v.sms}
                    </span>
                    <span className="block text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100">
                      {maskPhone(methods.phone)}
                    </span>
                  </span>
                </button>
              )}
              {methods.email && (
                <button
                  type="button"
                  onClick={() => onSubmitMethod('email')}
                  disabled={loading}
                  className="group flex items-center gap-3 text-left rounded-2xl px-4 py-3.5 portal-card hover:ring-2 hover:ring-[--ring-color] focus-visible:ring-2 focus-visible:ring-[--ring-color] focus-visible:outline-none transition-shadow disabled:opacity-50"
                  style={{ ['--ring-color' as any]: primaryColor } as React.CSSProperties}
                >
                  <span
                    className="size-9 rounded-xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${primaryColor}18`, color: primaryColor }}
                    aria-hidden="true"
                  >
                    <Icon icon={Icons.mail} className="size-5" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {v.email}
                    </span>
                    <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {maskEmail(methods.email)}
                    </span>
                  </span>
                </button>
              )}
              {!methods.phone && !methods.email && (
                <div className="flex items-center gap-3 rounded-2xl px-4 py-3.5 portal-card">
                  <Bars3BottomRightIcon
                    className="size-5 text-gray-400 dark:text-gray-500"
                    aria-hidden="true"
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400">{v.noMethods}</p>
                </div>
              )}
            </div>
            {(error || invalidCode) && (
              <p className="text-sm text-red-600 dark:text-red-400">
                {invalidCode ? v.invalidCode : error || v.error}
              </p>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default RequestAuth;
